import Cookies from "js-cookie";
import { createContext, useEffect, useRef, useState } from "react";
import { AuthUser, Country, SocialIcon } from "../../../shared/types";
import { CountryActions } from "../actions/country";
import { setLogoutCallback, setToken } from "../actions/_base";
import useGeoLocation from "react-ipgeolocation";
import { setCountry } from "./../actions/_base";
import { isDevEnv } from "./../helpers/functions";
import { DEFAULT_COUNTRIES, DEFAULT_COUNTRY } from "../helpers/defaults";

interface AppContent {
  user?: AuthUser;
  countries: Country[];
  location: Country;
  socialIcons: SocialIcon[];
}

interface IAppContext extends AppContent {
  setUser: (user?: AuthUser) => void;
  loading: boolean;
}

const DEFAULT: IAppContext = {
  location: DEFAULT_COUNTRY,
  countries: DEFAULT_COUNTRIES,
  setUser: () => undefined,
  loading: true,
  socialIcons: [
    {
      iconClass: "fa fa-facebook",
      shareURL: "https://www.facebook.com/karinazarfino447/",
      name: "Facebook",
    },
    {
      iconClass: "fa fa-instagram",
      shareURL: "https://www.instagram.com/karinazarfino/",
      name: "Instagram",
    },
    {
      iconClass: "fa fa-youtube",
      shareURL: "https://www.youtube.com/channel/UCuQ4osqxqprdYHO-BZr10LA",
      name: "YouTube",
    },
    {
      iconClass: "fa fa-vimeo",
      shareURL: "https://vimeo.com/user884753",
      name: "Vimeo",
    },
    {
      iconClass: " fa fa-linkedin",
      shareURL: "https://ar.linkedin.com/in/karinazarfino",
      name: "LinkedIn",
    },
    {
      iconClass: "fa fa-envelope",
      shareURL: "mailto:infokarinazarfino@gmail.com",
      name: "Mail",
    },
  ],
};

export const AppContext = createContext<IAppContext>(DEFAULT);

interface AppContextProviderProps {
  children?: React.ReactNode;
}

export const AppContextProvider = (props: AppContextProviderProps) => {
  const setUser = (user?: AuthUser) => {
    if (!user && !Cookies.get("user")) return;

    if (!user) Cookies.remove("user");
    else Cookies.set("user", JSON.stringify(user));
    setAppState((prev) => ({ ...prev, user }));
  };
  const [app, setAppState] = useState<IAppContext>({
    ...DEFAULT,
    setUser,
  });

  const { children } = props;

  const geoLocation = useGeoLocation();
  const search = window.location.search;
  const from = new URLSearchParams(search).get("from");
  const initStartedRef = useRef(false);

  const getCountry = (fromCode: string, countriesList: Country[]): Country => {
    const list =
      countriesList && countriesList.length > 0
        ? countriesList
        : DEFAULT_COUNTRIES;

    const findFallback = (): Country =>
      list.find((c) => c.code === "AR" && c.enabled) ||
      list.find((c) => c.isDefault && c.enabled) ||
      list.find((c) => c.code === "AR") ||
      list.find((c) => c.isDefault) ||
      list[0] ||
      DEFAULT_COUNTRY;

    if (!fromCode) return findFallback();

    const currentCountry = list.find((c) => c.code === fromCode && c.enabled);

    // If no country matching fromCode, return fallback
    if (!currentCountry) {
      return findFallback();
    }

    if (!currentCountry.enabled) {
      // If country is not enabled, we try to reroute
      if (currentCountry.reroute)
        return getCountry(currentCountry.reroute, list);
      // If country is not enabled and can't reroute we return fallback
      else return findFallback();
    }

    return currentCountry;
  };

  const initContext = async (targetCountryCode?: string) => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    if (
      window.location.protocol === "http:" &&
      !window.location.href.includes("localhost")
    ) {
      window.location.href = "https://karinazarfino.com";
      return;
    }

    let countries: Country[] = DEFAULT_COUNTRIES;
    try {
      const response = await CountryActions.getAll({});
      if (
        response &&
        !response.failed &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        countries = response.data;
      }
    } catch (e) {
      console.warn("Could not load countries from API, defaulting to Argentina", e);
    }

    const userCookie = Cookies.get("user");
    let user: AuthUser | undefined = undefined;

    if (userCookie) {
      try {
        user = JSON.parse(userCookie);
      } catch (e) {
        console.error("Invalid user cookie", e);
      }
    }

    const requestedCode =
      (isDevEnv() && from) || targetCountryCode || geoLocation?.country || "AR";
    const country = getCountry(requestedCode, countries);

    setLogoutCallback(() => {
      setUser();
    });

    setCountry(country);
    setToken(user?.token);
    setAppState((prev) => ({
      ...prev,
      user: user,
      countries,
      loading: false,
      location: country,
    }));
  };

  useEffect(() => {
    if (geoLocation?.country || from) {
      initContext(from || geoLocation?.country);
      return;
    }

    // Fallback timer: if geolocation is slow or blocked, initialize anyway with Argentina
    const timer = setTimeout(() => {
      initContext("AR");
    }, 1500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoLocation?.country, from]);

  return (
    <AppContext.Provider value={app}>
      {!app.loading && children}
    </AppContext.Provider>
  );
};
